import type { LolRoleKey } from "@/shared/ui/data-display/role-icon";

/**
 * Курируемая карта «чемпион → линии» (KAN-920).
 *
 * Зачем она вообще существует: **линии нет ни в одном автоматическом источнике.**
 * Data Dragon отдаёт `tags` — это КЛАСС персонажа (Fighter, Mage, Tank, Assassin,
 * Marksman, Support), Community Dragon и Meraki отдают то же самое. Подмена линии
 * классом — заведённый дефект KAN-896: «Лес» → Fighter/Tank/Assassin затягивает
 * Гарена с Мальфитом и теряет Иву.
 *
 * Собственные наблюдения (`ReplayPlayerStats.Position`) на 03.09.2026 — 15 игр
 * и 83 чемпиона, меньше двух наблюдений на чемпиона, и больше не станет
 * до релиза десктопного клиента (KAN-786). Поэтому карта ведётся руками.
 *
 * КАК ПРАВИТЬ. Ключ — идентификатор Data Dragon, а НЕ отображаемое имя:
 * `MonkeyKing`, а не `Wukong`; `Belveth`, а не `Bel'Veth`; `AurelionSol`,
 * а не `Aurelion Sol`. Иначе карта разойдётся с фильтром на первом же чемпионе
 * с апострофом. Тест сверяет ключи с реальным каталогом.
 *
 * ЧЕГО ДЕЛАТЬ НЕЛЬЗЯ. Дописывать чемпиона «чтобы покрытие было полным».
 * Выдуманная линия хуже отсутствующей: отсутствующая честно говорит «не знаем»
 * и оставляет чемпиона доступным, а выдуманная выглядит данными и уводит выбор.
 * Чемпион вне карты обязан находиться поиском и попадать в «Все».
 */

/**
 * Линия. Подмножество <see cref="LolRoleKey"/> — тех же ключей, что у иконок ролей,
 * чтобы не заводить третий набор строк.
 *
 * Отдельный тип от класса персонажа НАМЕРЕННО: «Support» существует и как линия,
 * и как класс Riot, и в одном модуле они неминуемо перепутаются. Здесь их разводит
 * компилятор, а не внимательность читающего.
 */
export type ChampionLane = Extract<
  LolRoleKey,
  "top" | "jungle" | "mid" | "adc" | "support"
>;

export const CHAMPION_LANES: Readonly<Record<string, readonly ChampionLane[]>> = {
  Aatrox: ["top"],
  Ahri: ["mid"],
  Akali: ["mid", "top"],
  Alistar: ["support"],
  Amumu: ["jungle"],
  Anivia: ["mid"],
  Annie: ["mid"],
  Ashe: ["adc"],
  AurelionSol: ["mid"],
  Azir: ["mid"],
  Bard: ["support"],
  Belveth: ["jungle"],
  Blitzcrank: ["support"],
  Brand: ["support", "mid"],
  Braum: ["support"],
  Caitlyn: ["adc"],
  Camille: ["top"],
  Cassiopeia: ["mid"],
  Chogath: ["top"],
  Corki: ["mid"],
  Darius: ["top"],
  Diana: ["jungle", "mid"],
  DrMundo: ["top", "jungle"],
  Draven: ["adc"],
  Ekko: ["jungle", "mid"],
  Elise: ["jungle"],
  Evelynn: ["jungle"],
  Ezreal: ["adc"],
  Fiddlesticks: ["jungle"],
  Fiora: ["top"],
  Fizz: ["mid"],
  Galio: ["mid", "support"],
  Gangplank: ["top"],
  Garen: ["top"],
  Gnar: ["top"],
  Gragas: ["jungle", "top"],
  Graves: ["jungle"],
  Gwen: ["top"],
  Hecarim: ["jungle"],
  Heimerdinger: ["mid", "top"],
  Illaoi: ["top"],
  Irelia: ["top", "mid"],
  Ivern: ["jungle"],
  Janna: ["support"],
  JarvanIV: ["jungle"],
  Jax: ["top", "jungle"],
  Jayce: ["top", "mid"],
  Jhin: ["adc"],
  Jinx: ["adc"],
  Kaisa: ["adc"],
  Kalista: ["adc"],
  Karma: ["support", "mid"],
  Karthus: ["jungle", "mid"],
  Kassadin: ["mid"],
  Katarina: ["mid"],
  Kayle: ["top"],
  Kayn: ["jungle"],
  Kennen: ["top"],
  Khazix: ["jungle"],
  Kindred: ["jungle"],
  Kled: ["top"],
  KogMaw: ["adc"],
  Leblanc: ["mid"],
  LeeSin: ["jungle"],
  Leona: ["support"],
  Lillia: ["jungle"],
  Lissandra: ["mid"],
  Lucian: ["adc"],
  Lulu: ["support"],
  Lux: ["support", "mid"],
  Malphite: ["top"],
  Malzahar: ["mid"],
  Maokai: ["support", "jungle"],
  MasterYi: ["jungle"],
  Milio: ["support"],
  MissFortune: ["adc"],
  Mordekaiser: ["top"],
  Morgana: ["support"],
  Nami: ["support"],
  Nasus: ["top"],
  Nautilus: ["support"],
  Neeko: ["mid", "support"],
  Nidalee: ["jungle"],
  Nilah: ["adc"],
  Nocturne: ["jungle"],
  Nunu: ["jungle"],
  Olaf: ["top", "jungle"],
  Orianna: ["mid"],
  Ornn: ["top"],
  Pantheon: ["top", "support"],
  Poppy: ["jungle", "top"],
  Pyke: ["support"],
  Qiyana: ["mid"],
  Quinn: ["top"],
  Rakan: ["support"],
  Rammus: ["jungle"],
  RekSai: ["jungle"],
  Rell: ["support"],
  Renekton: ["top"],
  Rengar: ["jungle", "top"],
  Riven: ["top"],
  Rumble: ["top"],
  Ryze: ["mid", "top"],
  Samira: ["adc"],
  Sejuani: ["jungle"],
  Senna: ["support", "adc"],
  Seraphine: ["support", "mid"],
  Sett: ["top"],
  Shaco: ["jungle"],
  Shen: ["top"],
  Shyvana: ["jungle"],
  Singed: ["top"],
  Sion: ["top"],
  Sivir: ["adc"],
  Skarner: ["jungle"],
  Sona: ["support"],
  Soraka: ["support"],
  Swain: ["support", "mid"],
  Sylas: ["mid", "top"],
  Syndra: ["mid"],
  TahmKench: ["support", "top"],
  Taliyah: ["jungle", "mid"],
  Talon: ["mid"],
  Taric: ["support"],
  Teemo: ["top"],
  Thresh: ["support"],
  Tristana: ["adc"],
  Trundle: ["jungle", "top"],
  Tryndamere: ["top"],
  TwistedFate: ["mid"],
  Twitch: ["adc"],
  Udyr: ["jungle"],
  Urgot: ["top"],
  Varus: ["adc"],
  Vayne: ["adc", "top"],
  Veigar: ["mid"],
  Velkoz: ["mid", "support"],
  Vex: ["mid"],
  Vi: ["jungle"],
  Viego: ["jungle"],
  Viktor: ["mid"],
  Vladimir: ["mid", "top"],
  Volibear: ["jungle", "top"],
  Warwick: ["jungle"],
  MonkeyKing: ["top", "jungle"],
  Xayah: ["adc"],
  Xerath: ["mid", "support"],
  XinZhao: ["jungle"],
  Yasuo: ["mid", "top"],
  Yone: ["mid", "top"],
  Yorick: ["top"],
  Yuumi: ["support"],
  Zac: ["jungle"],
  Zed: ["mid"],
  Zeri: ["adc"],
  Ziggs: ["mid", "adc"],
  Zilean: ["support", "mid"],
  Zoe: ["mid"],
  Zyra: ["support"],
  Akshan: ["mid", "top"],
  Ambessa: ["top"],
  Aphelios: ["adc"],
  Aurora: ["mid", "top"],
  Briar: ["jungle"],
  Hwei: ["mid"],
  KSante: ["top"],
  Naafiri: ["mid", "jungle"],
  Renata: ["support"],
  Smolder: ["adc"],
};

/** Линии чемпиона. Пустой массив — линия неизвестна, и это законное состояние. */
export function lanesOf(championId: string): readonly ChampionLane[] {
  return CHAMPION_LANES[championId] ?? [];
}

/**
 * Подходит ли чемпион под фильтр линии.
 *
 * Чемпион без записи в карте отвечает `false` для конкретной линии, но НЕ прячется
 * из списка «все линии» и находится поиском — это требование KAN-896:
 * отсутствие данных не должно выглядеть как отсутствие чемпиона.
 */
export function championPlaysLane(championId: string, lane: ChampionLane): boolean {
  return lanesOf(championId).includes(lane);
}

/** Известна ли линия — для честного показа покрытия. */
export function hasKnownLanes(championId: string): boolean {
  return lanesOf(championId).length > 0;
}

/**
 * Покрытие карты: сколько чемпионов из переданного каталога в ней есть.
 * Показывается пользователю числом — молчаливо неполная карта выглядит полной.
 */
export function laneCoverage(championIds: readonly string[]): {
  known: number;
  total: number;
} {
  return {
    known: championIds.filter(hasKnownLanes).length,
    total: championIds.length,
  };
}
